import { useEffect, useRef, useState } from "react";

import type WaveSurfer from "@wavesurfer/wavesurfer";

import { Text } from "./Typography";
import { markerSpacing } from "./tokens";

// Controlled external transport seek: a sibling surface (a conversation
// timestamp or a results-rail evidence link) bumps `nonce` to request a seek. Each nonce is applied exactly once, deferred
// until the waveform is ready.
export interface AudioPlayerSeekRequest {
  ms: number;
  nonce: number;
}

export interface AudioPlayerProps {
  decodeFallbackEnabled?: boolean;
  src: string;
  // Playback position, throttled to ~100ms, so a sibling surface can follow
  // the utterance being played.
  onTimeUpdate?: (ms: number) => void;
  onPlayingChange?: (playing: boolean) => void;
  // Precomputed, immutable waveform peaks (a normalized 0..1 amplitude
  // envelope). When supplied the waveform renders from these and skips
  // client-side decode of the media; when omitted an explicit Play may allow
  // WaveSurfer to decode the media element itself.
  peaks?: number[];
  onDecodeFallback?: () => void;
  seekRequest?: AudioPlayerSeekRequest | null;
}

type WaveSurferInstance = InstanceType<typeof WaveSurfer>;

const metadataReadyState = 1;
// One edge-to-edge strip: a 64px waveform, nothing under it.
const waveformHeight =
  Number.parseInt(markerSpacing[12], 10) +
  Number.parseInt(markerSpacing[4], 10);
const timeUpdateIntervalMs = 100;

function formatSeconds(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) {
    return "-:--";
  }

  const totalSeconds = Math.floor(seconds);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const paddedSeconds = String(totalSeconds % 60).padStart(2, "0");

  if (hours > 0) {
    const paddedMinutes = String(minutes).padStart(2, "0");
    return `${String(hours)}:${paddedMinutes}:${paddedSeconds}`;
  }

  return `${String(minutes)}:${paddedSeconds}`;
}

function isEditableTarget(target: EventTarget | null): boolean {
  if (!(target instanceof Element)) {
    return false;
  }
  if (target.closest('[role="dialog"], [role="menu"], [role="listbox"]')) {
    return true;
  }
  const editable = target.closest(
    'input, textarea, select, [contenteditable], [role="combobox"], ' +
      '[role="textbox"], [role="spinbutton"]',
  );
  return (
    editable != null && editable.getAttribute("contenteditable") !== "false"
  );
}

function readColorToken(element: HTMLElement, token: string): string {
  const value = window.getComputedStyle(element).getPropertyValue(token).trim();
  return value || token;
}

function errorMessage(error: unknown): string {
  if (error instanceof Error && error.message) {
    return error.message;
  }

  return "Audio unavailable.";
}

function mediaDuration(media: HTMLMediaElement): number | null {
  if (Number.isFinite(media.duration) && media.duration > 0) {
    return media.duration;
  }

  return null;
}

export function AudioPlayer({
  decodeFallbackEnabled = true,
  onDecodeFallback,
  onPlayingChange,
  onTimeUpdate,
  peaks,
  seekRequest,
  src,
}: AudioPlayerProps) {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const playButtonRef = useRef<HTMLButtonElement | null>(null);
  const metadataRef = useRef<{ duration: number; src: string } | null>(null);
  const waveformRef = useRef<HTMLDivElement | null>(null);
  const waveSurferRef = useRef<WaveSurferInstance | null>(null);
  const appliedSeekNonceRef = useRef<number | null>(null);
  const pendingPlaySrcRef = useRef<string | null>(null);
  const onDecodeFallbackRef = useRef(onDecodeFallback);
  const onTimeUpdateRef = useRef(onTimeUpdate);
  const onPlayingChangeRef = useRef(onPlayingChange);
  const lastTimeUpdateRef = useRef(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(Number.NaN);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [decodeFallbackSrc, setDecodeFallbackSrc] = useState<string | null>(
    null,
  );

  useEffect(() => {
    onDecodeFallbackRef.current = onDecodeFallback;
  }, [onDecodeFallback]);

  useEffect(() => {
    onTimeUpdateRef.current = onTimeUpdate;
    onPlayingChangeRef.current = onPlayingChange;
  }, [onPlayingChange, onTimeUpdate]);

  useEffect(() => {
    onPlayingChangeRef.current?.(isPlaying);
  }, [isPlaying]);

  // Space toggles play / pause from anywhere on the canvas except editable
  // controls and transient layers, which own their keys.
  useEffect(() => {
    const listener = (event: KeyboardEvent) => {
      if (
        event.key !== " " ||
        event.defaultPrevented ||
        event.repeat ||
        event.altKey ||
        event.ctrlKey ||
        event.metaKey ||
        event.shiftKey ||
        isEditableTarget(event.target)
      ) {
        return;
      }
      if (
        event.target instanceof HTMLElement &&
        event.target.closest('button, a, [role="button"]') &&
        !event.target.closest(".marker-audio-player__play")
      ) {
        // Space activates the focused control natively.
        return;
      }
      event.preventDefault();
      playButtonRef.current?.click();
    };
    document.addEventListener("keydown", listener);
    return () => {
      document.removeEventListener("keydown", listener);
    };
  }, []);

  useEffect(() => {
    onTimeUpdateRef.current = onTimeUpdate;
    onPlayingChangeRef.current = onPlayingChange;
  }, [onPlayingChange, onTimeUpdate]);

  useEffect(() => {
    onPlayingChangeRef.current?.(isPlaying);
  }, [isPlaying]);

  // Space toggles play / pause from anywhere on the canvas except editable
  // controls and transient layers, which own their keys.
  useEffect(() => {
    const listener = (event: KeyboardEvent) => {
      if (
        event.key !== " " ||
        event.defaultPrevented ||
        event.repeat ||
        event.altKey ||
        event.ctrlKey ||
        event.metaKey ||
        event.shiftKey ||
        isEditableTarget(event.target)
      ) {
        return;
      }
      if (
        event.target instanceof HTMLElement &&
        event.target.closest('button, a, [role="button"]') &&
        !event.target.closest(".marker-audio-player__play")
      ) {
        // Space activates the focused control natively.
        return;
      }
      event.preventDefault();
      playButtonRef.current?.click();
    };
    document.addEventListener("keydown", listener);
    return () => {
      document.removeEventListener("keydown", listener);
    };
  }, []);

  // External transport seek (`seekRequest` controlled prop): apply each nonce
  // exactly once, waiting for readiness so a request issued before the
  // waveform decodes still lands.
  useEffect(() => {
    if (!seekRequest || seekRequest.nonce === appliedSeekNonceRef.current) {
      return;
    }
    const waveSurfer = waveSurferRef.current;
    if (!waveSurfer || !isReady || loadError) {
      return;
    }
    appliedSeekNonceRef.current = seekRequest.nonce;
    const nextTime = seekRequest.ms / 1000;
    waveSurfer.setTime(nextTime);
    setCurrentTime(nextTime);
    onTimeUpdateRef.current?.(seekRequest.ms);
  }, [seekRequest, isReady, loadError]);

  useEffect(() => {
    const container = waveformRef.current;
    const media = audioRef.current;
    if (!container || !media) {
      return undefined;
    }
    const containerElement = container;
    const mediaElement = media;

    let cancelled = false;
    let destroyWaveSurfer: (() => void) | undefined;
    let didInitialize = false;
    const hasPrecomputedPeaks = peaks != null && peaks.length > 0;
    const decodeFallbackRequested = decodeFallbackSrc === src;

    setCurrentTime(0);
    setDuration(Number.NaN);
    setIsPlaying(false);
    setIsReady(false);
    setLoadError(null);

    function handleMediaError() {
      setLoadError("Audio unavailable.");
      setIsPlaying(false);
      setIsReady(false);
    }

    async function initializeWaveSurfer(audioDuration: number) {
      if (didInitialize) {
        return;
      }
      didInitialize = true;

      // `@wavesurfer/wavesurfer` is an npm alias for `wavesurfer.js` (see
      // package.json) — the scoped package name is unpublished upstream.
      const module = await import("@wavesurfer/wavesurfer");
      if (cancelled) {
        return;
      }

      // WaveSurfer's pre-decoded peaks API: a supplied single-channel envelope
      // (`[peaks]`, mirrored top/bottom by the renderer) renders the waveform
      // and skips fetching/decoding the media; `undefined` lets WaveSurfer
      // decode the media element itself. No synthesized fallback.
      const decodedPeaks =
        peaks != null && peaks.length > 0 ? [peaks] : undefined;

      const waveSurfer = module.default.create({
        backend: "MediaElement",
        barRadius: 0,
        container: containerElement,
        cursorColor: readColorToken(
          containerElement,
          "--marker-color-on-surface-variant",
        ),
        cursorWidth: 1,
        dragToSeek: false,
        duration: audioDuration,
        height: waveformHeight,
        hideScrollbar: true,
        interact: true,
        media: mediaElement,
        mediaControls: false,
        normalize: true,
        peaks: decodedPeaks,
        progressColor: readColorToken(
          containerElement,
          "--marker-color-primary",
        ),
        waveColor: readColorToken(
          containerElement,
          "--marker-color-outline-variant",
        ),
      });

      waveSurferRef.current = waveSurfer;

      const subscriptions = [
        waveSurfer.on("ready", (readyDuration) => {
          setDuration(readyDuration);
          setIsReady(true);
          setLoadError(null);
          if (pendingPlaySrcRef.current === src) {
            pendingPlaySrcRef.current = null;
            void waveSurfer.playPause().catch((error: unknown) => {
              setLoadError(errorMessage(error));
              setIsPlaying(false);
            });
          }
        }),
        waveSurfer.on("decode", (decodedDuration) => {
          setDuration(decodedDuration);
        }),
        waveSurfer.on("timeupdate", (nextTime) => {
          setCurrentTime(nextTime);
          const now = Date.now();
          if (now - lastTimeUpdateRef.current >= timeUpdateIntervalMs) {
            lastTimeUpdateRef.current = now;
            onTimeUpdateRef.current?.(Math.round(nextTime * 1000));
          }
        }),
        waveSurfer.on("interaction", (nextTime) => {
          setCurrentTime(nextTime);
          onTimeUpdateRef.current?.(Math.round(nextTime * 1000));
        }),
        waveSurfer.on("play", () => {
          setIsPlaying(true);
        }),
        waveSurfer.on("pause", () => {
          setIsPlaying(false);
        }),
        waveSurfer.on("finish", () => {
          setIsPlaying(false);
          setCurrentTime(waveSurfer.getDuration());
        }),
        waveSurfer.on("error", (error) => {
          setLoadError(errorMessage(error));
          setIsPlaying(false);
          setIsReady(false);
        }),
      ];

      destroyWaveSurfer = () => {
        for (const unsubscribe of subscriptions) {
          unsubscribe();
        }
        waveSurfer.destroy();
      };
    }

    function initializeRequestedWaveSurfer(audioDuration: number) {
      if (!hasPrecomputedPeaks && !decodeFallbackRequested) {
        return;
      }
      void initializeWaveSurfer(audioDuration).catch((error: unknown) => {
        if (!cancelled) {
          setLoadError(errorMessage(error));
          setIsReady(false);
        }
      });
    }

    function handleLoadedMetadata() {
      const audioDuration = mediaDuration(mediaElement);
      if (audioDuration == null) {
        setLoadError("Audio metadata unavailable.");
        setIsReady(false);
        return;
      }

      setDuration(audioDuration);
      metadataRef.current = { duration: audioDuration, src };
      initializeRequestedWaveSurfer(audioDuration);
    }

    mediaElement.addEventListener("error", handleMediaError);
    mediaElement.addEventListener("durationchange", handleLoadedMetadata);
    mediaElement.addEventListener("loadedmetadata", handleLoadedMetadata);
    if (mediaElement.error != null) {
      handleMediaError();
    } else if (mediaElement.readyState >= metadataReadyState) {
      handleLoadedMetadata();
    } else if (metadataRef.current?.src === src) {
      setDuration(metadataRef.current.duration);
      initializeRequestedWaveSurfer(metadataRef.current.duration);
    }

    return () => {
      cancelled = true;
      mediaElement.removeEventListener("error", handleMediaError);
      mediaElement.removeEventListener("durationchange", handleLoadedMetadata);
      mediaElement.removeEventListener("loadedmetadata", handleLoadedMetadata);
      destroyWaveSurfer?.();
      waveSurferRef.current = null;
    };
  }, [decodeFallbackSrc, peaks, src]);

  function handlePlayPause() {
    const waveSurfer = waveSurferRef.current;
    if (!waveSurfer) {
      if (
        (peaks == null || peaks.length === 0) &&
        decodeFallbackEnabled &&
        decodeFallbackSrc !== src &&
        Number.isFinite(duration) &&
        duration > 0 &&
        !loadError
      ) {
        pendingPlaySrcRef.current = src;
        onDecodeFallbackRef.current?.();
        setDecodeFallbackSrc(src);
      }
      return;
    }

    void waveSurfer.playPause().catch((error: unknown) => {
      setLoadError(errorMessage(error));
      setIsPlaying(false);
    });
  }

  const hasPrecomputedPeaks = peaks != null && peaks.length > 0;
  const isDecodeFallbackPending =
    !hasPrecomputedPeaks && decodeFallbackSrc === src && !isReady;
  const canRequestDecodeFallback =
    !hasPrecomputedPeaks &&
    decodeFallbackEnabled &&
    decodeFallbackSrc !== src &&
    Number.isFinite(duration) &&
    duration > 0;

  return (
    <div className="marker-audio-player">
      <audio
        aria-hidden="true"
        crossOrigin="use-credentials"
        preload="metadata"
        ref={audioRef}
        src={src}
      />
      <button
        aria-label={isPlaying ? "Pause" : "Play"}
        className="marker-audio-player__play"
        disabled={
          Boolean(loadError) ||
          isDecodeFallbackPending ||
          (!isReady && !canRequestDecodeFallback)
        }
        onClick={handlePlayPause}
        ref={playButtonRef}
        type="button"
      >
        {isPlaying ? (
          <svg aria-hidden="true" height="14" viewBox="0 0 14 14" width="14">
            <rect fill="currentColor" height="14" width="4" x="2" y="0" />
            <rect fill="currentColor" height="14" width="4" x="8" y="0" />
          </svg>
        ) : (
          <svg aria-hidden="true" height="14" viewBox="0 0 14 14" width="14">
            <path d="M2 0 L13 7 L2 14 Z" fill="currentColor" />
          </svg>
        )}
      </button>
      <div className="marker-audio-player__stage">
        <div
          aria-busy={
            !loadError &&
            (isDecodeFallbackPending || (hasPrecomputedPeaks && !isReady))
          }
          aria-label="Audio waveform"
          className="marker-audio-player__waveform"
          ref={waveformRef}
          role="group"
        />
        <Text
          as="span"
          className="marker-audio-player__time marker-audio-player__time--current"
          variant="small"
        >
          {formatSeconds(currentTime)}
        </Text>
        <Text
          as="span"
          className="marker-audio-player__time marker-audio-player__time--total"
          variant="small"
        >
          {formatSeconds(duration)}
        </Text>
      </div>
      {!hasPrecomputedPeaks &&
      !isReady &&
      !isDecodeFallbackPending &&
      decodeFallbackEnabled &&
      !loadError ? (
        <Text as="span" className="marker-audio-player__hint" variant="muted">
          Play to load waveform
        </Text>
      ) : null}
      {loadError ? (
        <Text as="span" className="marker-audio-player__error" variant="small">
          {loadError}
        </Text>
      ) : null}
    </div>
  );
}
